import { useEffect, useMemo, useState } from 'react';
import { collection, doc, limit, onSnapshot, orderBy, query, where, type Unsubscribe } from 'firebase/firestore';
import { db } from '../utils/firebase';
import type { DebitCardAssignment, QBExpense } from '../types';
import { isAdminOnlyExpense } from '../utils/shortTermOps';
import { canManageCardExpensesOnBehalf, expenseBelongsToUser } from '../utils/cardExpenseAccess';
import { hasSalesCrmAccess } from '../utils/employeeDesignations';
import { dedupeCardExpenses } from '../utils/cardExpenseDedup';
import { mergePendingExpenseSources, unionPendingExpenseSources } from '../utils/cardExpensePendingMerge';
import { qbExpensesAccountDiscoveryQuery } from '../utils/qbExpensesQueries';

const DEBIT_CARDS_DOC = 'financeSettings/debitCards';

type PendingExpense = QBExpense & { id: string };

function mapExpenseDoc(id: string, raw: Record<string, unknown>): PendingExpense {
  return {
    id,
    ...raw,
    syncedAt: (raw.syncedAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    createdAt: (raw.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
  } as PendingExpense;
}

export function useDebitCardRegistry() {
  const [cards, setCards] = useState<DebitCardAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = doc(db, DEBIT_CARDS_DOC);
    return onSnapshot(ref, snap => {
      const data = snap.data() as { cards?: DebitCardAssignment[] } | undefined;
      setCards(data?.cards || []);
      setLoading(false);
    });
  }, []);

  return { cards, loading };
}

/** Active card assignments for the signed-in user. */
export function useUserCardAssignments(userId: string | undefined) {
  const { cards, loading } = useDebitCardRegistry();
  const userCards = useMemo(
    () => (userId ? cards.filter(c => c.isActive && c.assignedUserId === userId) : []),
    [cards, userId],
  );
  return { userCards, loading };
}

export function usePendingCardExpenses(
  userId: string | undefined,
  options?: { viewAll?: boolean; userCards?: DebitCardAssignment[] },
) {
  const viewAll = options?.viewAll === true;
  const { cards: registryCards } = useDebitCardRegistry();
  const optionUserCards = options?.userCards;
  const userCards = useMemo(
    () =>
      optionUserCards ??
      (userId ? registryCards.filter(c => c.isActive && c.assignedUserId === userId) : []),
    [optionUserCards, registryCards, userId],
  );
  const [expenses, setExpenses] = useState<PendingExpense[]>([]);
  const [loading, setLoading] = useState(true);

  const cardLastFours = useMemo(
    () => Array.from(new Set(userCards.map(c => c.lastFour).filter(Boolean))),
    [userCards],
  );

  useEffect(() => {
    if (!viewAll && !userId) {
      setExpenses([]);
      setLoading(false);
      return;
    }

    /** Per-query docs so a charge that leaves one pending query is dropped. */
    const sources = new Map<string, Map<string, PendingExpense>>();
    const unsubs: Unsubscribe[] = [];

    const publishFromSources = () => {
      const merged = unionPendingExpenseSources(sources);
      const rows = Array.from(merged.values()).filter(row => {
        // Short Term Ops is admin-only — never list it for non-admin card inboxes.
        // Admins still get a separate weekday digest for those rows.
        if (isAdminOnlyExpense(row)) return false;
        // viewAll (admin / office ops): include unassigned pending so the digest is actionable.
        if (viewAll) return true;
        return expenseBelongsToUser(row, registryCards, userId!);
      });
      rows.sort((a, b) => (a.txnDate < b.txnDate ? 1 : -1));
      setExpenses(dedupeCardExpenses(rows));
      setLoading(false);
    };

    if (viewAll) {
      unsubs.push(
        onSnapshot(query(collection(db, 'qbExpenses'), where('categorizationStatus', '==', 'pending')), snap => {
          mergePendingExpenseSources(
            sources,
            'all-pending',
            snap.docs.map(d => mapExpenseDoc(d.id, d.data())),
          );
          publishFromSources();
        }),
      );
      return () => unsubs.forEach(u => u());
    }

    unsubs.push(
      onSnapshot(
        query(
          collection(db, 'qbExpenses'),
          where('assignedUserId', '==', userId),
          where('categorizationStatus', '==', 'pending'),
        ),
        snap => {
          mergePendingExpenseSources(
            sources,
            'assigned',
            snap.docs.map(d => mapExpenseDoc(d.id, d.data())),
          );
          publishFromSources();
        },
      ),
    );

    for (const lastFour of cardLastFours) {
      unsubs.push(
        onSnapshot(
          query(
            collection(db, 'qbExpenses'),
            where('cardLastFour', '==', lastFour),
            where('categorizationStatus', '==', 'pending'),
          ),
          snap => {
            mergePendingExpenseSources(
              sources,
              `card:${lastFour}`,
              snap.docs
                .map(d => mapExpenseDoc(d.id, d.data()))
                .filter(row => expenseBelongsToUser(row, registryCards, userId!)),
            );
            publishFromSources();
          },
        ),
      );
    }

    return () => unsubs.forEach(u => u());
  }, [userId, viewAll, cardLastFours, registryCards]);

  return { expenses, count: expenses.length, loading };
}

/**
 * Most recent charges the user categorized (or all, for admins) so the inbox
 * can show whether each save actually landed in QuickBooks.
 */
export function useRecentlyCategorizedCardExpenses(
  userId: string | undefined,
  options?: { viewAll?: boolean; max?: number },
) {
  const viewAll = options?.viewAll === true;
  const max = options?.max ?? 10;
  const queryLimit = Math.min(max * 3, 45);
  const [expenses, setExpenses] = useState<PendingExpense[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!viewAll && !userId) {
      setExpenses([]);
      setLoading(false);
      return;
    }
    const q = viewAll
      ? query(
          collection(db, 'qbExpenses'),
          where('categorizationStatus', '==', 'categorized'),
          orderBy('categorizedAt', 'desc'),
          limit(queryLimit),
        )
      : query(
          collection(db, 'qbExpenses'),
          where('categorizedBy', '==', userId),
          orderBy('categorizedAt', 'desc'),
          limit(queryLimit),
        );
    return onSnapshot(
      q,
      snap => {
        setExpenses(
          dedupeCardExpenses(snap.docs.map(d => mapExpenseDoc(d.id, d.data())))
            .filter(row => !isAdminOnlyExpense(row))
            .slice(0, max),
        );
        setLoading(false);
      },
      () => {
        setExpenses([]);
        setLoading(false);
      },
    );
  }, [userId, viewAll, max, queryLimit]);

  return { expenses, loading };
}

const INTERNAL_CARD_EXPENSE_ROLES = new Set(['admin', 'employee', 'staff']);

export function isCardExpensesNavRole(role: string | undefined): boolean {
  return !!role && INTERNAL_CARD_EXPENSE_ROLES.has(role);
}

export function useCardExpenseAccess(
  userId: string | undefined,
  role?: string,
  designations?: import('../types').EmployeeDesignation[] | string[],
) {
  const canManageOnBehalf = canManageCardExpensesOnBehalf({
    authRole: role,
    designations,
  });
  const { userCards, loading: cardsLoading } = useUserCardAssignments(userId);
  const { count, loading: pendingLoading } = usePendingCardExpenses(userId, {
    viewAll: canManageOnBehalf,
    userCards,
  });
  const hasCardAssignment = userCards.length > 0;
  const hasPending = count > 0;
  const canAccess =
    hasCardAssignment
    || hasPending
    || isCardExpensesNavRole(role)
    || canManageOnBehalf
    || hasSalesCrmAccess({ authRole: role, designations });
  return {
    canAccess,
    showInNav: canAccess,
    hasCardAssignment,
    pendingCount: count,
    loading: cardsLoading || pendingLoading,
  };
}

/** Distinct bank account names from recent QB expenses (for admin registry). */
export function useDiscoveredBankAccounts() {
  const [accounts, setAccounts] = useState<{ name: string; count: number; lastFour: string | null }[]>([]);

  useEffect(() => {
    const q = qbExpensesAccountDiscoveryQuery();
    return onSnapshot(q, snap => {
      const map = new Map<string, number>();
      snap.forEach(docSnap => {
        const name = docSnap.data().bankAccountName as string | undefined;
        if (!name?.trim()) return;
        map.set(name, (map.get(name) || 0) + 1);
      });
      const rows = Array.from(map.entries())
        .map(([name, count]) => ({
          name,
          count,
          lastFour: name.match(/(\d{4})\s*$/)?.[1] || name.match(/\d{4}/g)?.slice(-1)[0] || null,
        }))
        .sort((a, b) => b.count - a.count);
      setAccounts(rows);
    });
  }, []);

  return accounts;
}
